/**
 * Etapa 3 - Comissao e cobranca: percentual, modelo de cobranca e politica de
 * estorno da empresa, com as regras da comissao escritas por extenso.
 *
 * Decisoes nao obvias:
 *  - O percentual e digitado como texto ("15,00") e vira bps por conta de
 *    string (assistente.ts); ao sair do campo o texto e normalizado.
 *  - O modelo de cobranca mostra as tres opcoes com a nota fixa de "decisao
 *    pendente do dono": a tela registra o combinado sem fingir que a regra
 *    geral ja existe.
 *  - A politica de estorno nasce no PADRAO decidido (reembolso nao reverte,
 *    chargeback reverte) e fica marcada como tal; as outras duas existem
 *    porque a politica e por empresa (companies.refund_policy).
 *  - A simulacao usa uma venda de exemplo em centavos inteiros e mostra o que
 *    acontece num reembolso e num chargeback sob a politica escolhida: e a
 *    pergunta que o lojista faz na primeira reuniao.
 *  - Tudo aqui pode mudar depois na aba "Comissão e cobrança" da empresa: a
 *    etapa diz isso, para ninguem travar o cadastro por causa de um numero.
 */

import { useState } from 'react';
import { Badge, Field, Fieldset, Icon, Input, Panel } from '../../../../components/ui';
import { formatBRLFromCents, formatPercentFromBps } from '../../../../lib/format';
import type { BillingModel, RefundPolicy } from '../../../../types';
import { bpsParaTexto, textoParaBps } from '../../assistente';
import { MODELOS_COBRANCA, MODELO_COBRANCA, NOTA_MODELO_PENDENTE, POLITICAS_ESTORNO, POLITICA_ESTORNO, POLITICA_PADRAO, REGRAS_COMISSAO } from '../../vocabulario';
import { erroDe, type EtapaProps } from './props';

const EXEMPLO_VENDA_CENTS = 49_700;

function reverte(politica: RefundPolicy, motivo: 'reembolso' | 'chargeback'): boolean {
  if (politica === 'reverte_tudo') {
    return true;
  }
  if (politica === 'reverte_chargeback') {
    return motivo === 'chargeback';
  }
  return false;
}

export function EtapaComissao(props: EtapaProps): JSX.Element {
  const { form, onChange } = props;
  const [texto, setTexto] = useState(() => bpsParaTexto(form.feeBps));

  const aoDigitar = (valor: string): void => {
    setTexto(valor);
    const bps = textoParaBps(valor);
    if (bps !== null) {
      onChange({ feeBps: bps });
    }
  };

  const aoSair = (): void => {
    const bps = textoParaBps(texto);
    setTexto(bpsParaTexto(bps ?? form.feeBps));
  };

  const comissaoExemplo = Math.round((EXEMPLO_VENDA_CENTS * form.feeBps) / 10_000);
  // Texto que nao vira percentual ("100", "15,555") nao muda o formulario: sem
  // este aviso a pessoa seguiria achando que digitou 100% e criaria com 15%.
  const erroTexto = textoParaBps(texto) === null ? `Não entendi esse percentual: use até duas casas, como 15 ou 15,50. Vale o último válido, ${formatPercentFromBps(form.feeBps)}.` : null;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <Fieldset legend="Percentual de comissão" description="Sobre o valor pago de cada venda recuperada (ADR-001). Fica congelado em cada conversão: mudar depois não altera o passado.">
          <Field label="Percentual" required hint="Entre 1,00% e 50,00%. Use vírgula para os centésimos." error={erroTexto ?? erroDe(props, 'feeBps')}>
            {({ id, describedBy, invalid }) => (
              <div className="relative">
                <Input id={id} aria-describedby={describedBy} aria-invalid={invalid} inputMode="decimal" value={texto} onChange={(e) => aoDigitar(e.target.value)} onBlur={aoSair} className="pr-10 tabular-nums" autoComplete="off" />
                <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-3 grid place-items-center text-sm text-ink-subtle">
                  %
                </span>
              </div>
            )}
          </Field>
          <Panel tone="elevated" className="flex flex-col gap-1 p-4 text-sm">
            <p className="flex flex-wrap items-center gap-2 text-ink">
              <Badge tone="brand">{formatPercentFromBps(form.feeBps)}</Badge>
              <span className="text-ink-muted">sobre o valor recuperado</span>
            </p>
            <p className="text-xs text-ink-subtle">
              Exemplo: numa venda recuperada de {formatBRLFromCents(EXEMPLO_VENDA_CENTS)}, a comissão é <strong className="text-ink">{formatBRLFromCents(comissaoExemplo)}</strong> e a empresa fica com{' '}
              <strong className="text-ink">{formatBRLFromCents(EXEMPLO_VENDA_CENTS - comissaoExemplo)}</strong>.
            </p>
          </Panel>
        </Fieldset>

        <Fieldset legend="Modelo de cobrança" description="Como a comissão chega ao Converte Fácil.">
          <div role="radiogroup" aria-label="Modelo de cobrança" className="flex flex-col gap-2">
            {MODELOS_COBRANCA.map((modelo) => (
              <OpcaoModelo key={modelo} modelo={modelo} checked={form.billingModel === modelo} onChange={() => onChange({ billingModel: modelo })} />
            ))}
          </div>
          <Panel tone="warning" className="p-3 text-xs text-ink-muted">
            {NOTA_MODELO_PENDENTE}
          </Panel>
        </Fieldset>
      </div>

      <Fieldset legend="Política de estorno" description="O que acontece com a comissão quando uma venda recuperada é estornada. A regra decidida vale para todas as empresas; troque só se foi combinado diferente com esta.">
        <div role="radiogroup" aria-label="Política de estorno" className="grid gap-2 md:grid-cols-3">
          {POLITICAS_ESTORNO.map((politica) => {
            const info = POLITICA_ESTORNO[politica];
            const marcada = form.refundPolicy === politica;
            return (
              <label
                key={politica}
                className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors ${marcada ? 'border-brand/50 bg-brand/5' : 'border-line bg-surface hover:border-edge'}`}
              >
                <input
                  type="radio"
                  name="refundPolicy"
                  value={politica}
                  checked={marcada}
                  onChange={() => onChange({ refundPolicy: politica })}
                  className="mt-1 h-4 w-4 shrink-0 accent-[rgb(var(--color-brand))]"
                />
                <span className="min-w-0">
                  <span className="flex flex-wrap items-center gap-2 text-sm font-medium text-ink">
                    {info.curto}
                    {politica === POLITICA_PADRAO ? <Badge tone="good">Padrão decidido</Badge> : null}
                  </span>
                  <span className="mt-0.5 block text-xs text-ink-subtle">{info.explica}</span>
                </span>
              </label>
            );
          })}
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <SimulacaoEstorno motivo="reembolso" politica={form.refundPolicy} comissaoCents={comissaoExemplo} />
          <SimulacaoEstorno motivo="chargeback" politica={form.refundPolicy} comissaoCents={comissaoExemplo} />
        </div>
      </Fieldset>

      <Panel className="flex flex-col gap-3 p-5">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
          <Icon name="escudo" size="sm" className="text-brand" />
          Como a comissão é apurada (vale para toda empresa)
        </h3>
        <ul className="grid gap-2 md:grid-cols-2">
          {REGRAS_COMISSAO.map((regra) => (
            <li key={regra} className="flex items-start gap-2 text-xs text-ink-muted">
              <Icon name="check" size="xs" className="mt-0.5 text-status-good" />
              <span>{regra}</span>
            </li>
          ))}
        </ul>
        <p className="flex items-start gap-2 rounded-lg border border-dashed border-line px-3 py-2 text-xs text-ink-muted">
          <Icon name="editar" size="xs" className="mt-0.5 text-info" />
          <span>
            Percentual, modelo de cobrança e política de estorno podem ser alterados depois, na aba <strong className="text-ink">Comissão e cobrança</strong> da empresa. A mudança
            aparece na hora para a empresa e vale para as próximas vendas recuperadas.
          </span>
        </p>
      </Panel>
    </div>
  );
}

function SimulacaoEstorno({ motivo, politica, comissaoCents }: { readonly motivo: 'reembolso' | 'chargeback'; readonly politica: RefundPolicy; readonly comissaoCents: number }): JSX.Element {
  const volta = reverte(politica, motivo);
  return (
    <div className="flex items-start gap-2 rounded-lg border border-line bg-elevated px-3 py-2 text-xs">
      <Icon name={volta ? 'atualizar' : 'check'} size="xs" className={`mt-0.5 ${volta ? 'text-status-warning' : 'text-status-good'}`} />
      <span className="text-ink-muted">
        Se a venda de exemplo tiver <strong className="text-ink">{motivo === 'reembolso' ? 'reembolso total' : 'chargeback'}</strong>: a comissão de{' '}
        <strong className="text-ink">{formatBRLFromCents(comissaoCents)}</strong> {volta ? 'é revertida.' : 'permanece.'}
      </span>
    </div>
  );
}

function OpcaoModelo({ modelo, checked, onChange }: { readonly modelo: BillingModel; readonly checked: boolean; readonly onChange: () => void }): JSX.Element {
  const info = MODELO_COBRANCA[modelo];
  return (
    <label className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors ${checked ? 'border-brand/50 bg-brand/5' : 'border-line bg-surface hover:border-edge'}`}>
      <input type="radio" name="billingModel" value={modelo} checked={checked} onChange={onChange} className="mt-1 h-4 w-4 shrink-0 accent-[rgb(var(--color-brand))]" />
      <span className="min-w-0">
        <span className="flex flex-wrap items-center gap-2 text-sm font-medium text-ink">
          {info.label}
          <Badge tone={info.tone}>{info.curto}</Badge>
        </span>
        <span className="mt-0.5 block text-xs text-ink-subtle">{info.explicacao}</span>
      </span>
    </label>
  );
}
